import * as React from 'react';
import { useBacs } from '../../hooks/useBacs';
import { usePlanning } from '../../hooks/usePlanning';
import { useAuth } from '../../core/context/ProvidersContext';
import {
  computeActions, ACTION_LABELS, IActionItem, ActionType, ActionSeverity,
} from './computeActions';
import { BacActionModal } from './BacActionModal';
import { BacActionType } from '../../models';
import styles from './ActionsDashboard.module.scss';

const SEVERITY_LABEL: Record<ActionSeverity, string> = {
  high: 'Urgent',
  medium: 'À traiter',
  low: 'À planifier',
};

export const ActionsDashboard: React.FC = () => {
  const { hasRole } = useAuth();
  const isAdmin = hasRole('admin');

  const { bacs, loading: l1, refresh: refreshBacs } = useBacs();
  const { entries, loading: l3, refresh: refreshPlanning } = usePlanning();

  const [filterType, setFilterType] = React.useState<ActionType | 'all'>('all');
  const [filterSeverity, setFilterSeverity] = React.useState<ActionSeverity | 'all'>('all');
  const [filterDepot, setFilterDepot] = React.useState<string>('all');
  const [modalAction, setModalAction] = React.useState<IActionItem | null>(null);

  const actions = React.useMemo(
    () => computeActions({ bacs, planning: entries }),
    [bacs, entries]
  );

  const countsByType = React.useMemo(() => {
    const c: Record<string, number> = {};
    for (const a of actions) {
      c[a.type] = (c[a.type] || 0) + 1;
    }
    return c;
  }, [actions]);

  const countsBySeverity = React.useMemo(() => {
    const c: Record<ActionSeverity, number> = { high: 0, medium: 0, low: 0 };
    for (const a of actions) c[a.severity]++;
    return c;
  }, [actions]);

  const filtered = React.useMemo(() => {
    return actions.filter(a => {
      if (filterType !== 'all' && a.type !== filterType) return false;
      if (filterSeverity !== 'all' && a.severity !== filterSeverity) return false;
      if (filterDepot !== 'all' && a.depotTitle !== filterDepot) return false;
      return true;
    });
  }, [actions, filterType, filterSeverity, filterDepot]);

  const grouped = React.useMemo(() => {
    const groups: Record<string, IActionItem[]> = {};
    for (const a of filtered) {
      if (!groups[a.type]) groups[a.type] = [];
      groups[a.type].push(a);
    }
    return groups;
  }, [filtered]);

  const refreshAll = (): void => {
    refreshBacs();
    refreshPlanning();
  };

  const handleResolve = (action: IActionItem): void => {
    if (!isAdmin) return;
    if (action.type === 'relance_depot' || action.type === 'info_clients') {
      setModalAction(action);
      return;
    }
    if (action.type === 'intervention_imminente' || action.type === 'planning_manquant') {
      alert('Consulte l\'onglet Planning pour gérer cette action.');
    }
  };

  const handleModalSaved = (): void => {
    setModalAction(null);
    refreshAll();
  };

  const loading = l1 || l3;
  if (loading) return <div className={styles.loading}>Analyse des actions…</div>;

  const depotNames = Array.from(new Set(bacs.map(b => b.DepotTitle || ''))).filter(Boolean).sort();

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <div>
          <h2 className={styles.title}>Actions à faire</h2>
          <p className={styles.subtitle}>
            {actions.length} action{actions.length > 1 ? 's' : ''} identifiée{actions.length > 1 ? 's' : ''} automatiquement
            depuis l'état des bacs, restrictions et planning
          </p>
        </div>
      </div>

      <div className={styles.kpiStrip}>
        <button
          className={`${styles.kpiCard} ${styles.kpiHigh} ${filterSeverity === 'high' ? styles.kpiActive : ''}`}
          onClick={() => setFilterSeverity(filterSeverity === 'high' ? 'all' : 'high')}
        >
          <div className={styles.kpiPulse} />
          <div className={styles.kpiValue}>{countsBySeverity.high}</div>
          <div className={styles.kpiLabel}>Urgentes</div>
        </button>
        <button
          className={`${styles.kpiCard} ${styles.kpiMed} ${filterSeverity === 'medium' ? styles.kpiActive : ''}`}
          onClick={() => setFilterSeverity(filterSeverity === 'medium' ? 'all' : 'medium')}
        >
          <div className={styles.kpiValue}>{countsBySeverity.medium}</div>
          <div className={styles.kpiLabel}>À traiter</div>
        </button>
        <button
          className={`${styles.kpiCard} ${styles.kpiLow} ${filterSeverity === 'low' ? styles.kpiActive : ''}`}
          onClick={() => setFilterSeverity(filterSeverity === 'low' ? 'all' : 'low')}
        >
          <div className={styles.kpiValue}>{countsBySeverity.low}</div>
          <div className={styles.kpiLabel}>À planifier</div>
        </button>
        <div className={`${styles.kpiCard} ${styles.kpiTotal}`}>
          <div className={styles.kpiValue}>{actions.length}</div>
          <div className={styles.kpiLabel}>Total</div>
        </div>
      </div>

      <div className={styles.filters}>
        <div className={styles.filterPills}>
          <button
            className={`${styles.pill} ${filterType === 'all' ? styles.pillActive : ''}`}
            onClick={() => setFilterType('all')}
          >
            Toutes <span className={styles.pillCount}>{actions.length}</span>
          </button>
          {(Object.keys(ACTION_LABELS) as ActionType[]).map(type => {
            const count = countsByType[type] || 0;
            if (count === 0) return null;
            const meta = ACTION_LABELS[type];
            return (
              <button
                key={type}
                className={`${styles.pill} ${filterType === type ? styles.pillActive : ''}`}
                onClick={() => setFilterType(filterType === type ? 'all' : type)}
                style={filterType === type ? { borderColor: meta.color, color: meta.color } : undefined}
              >
                <span className={styles.pillIcon}>{meta.icon}</span>
                {meta.label}
                <span className={styles.pillCount}>{count}</span>
              </button>
            );
          })}
        </div>
        <select
          className={styles.depotSelect}
          value={filterDepot}
          onChange={e => setFilterDepot(e.target.value)}
        >
          <option value="all">Tous les dépôts</option>
          {depotNames.map(d => <option key={d} value={d}>{d}</option>)}
        </select>
      </div>

      {filtered.length === 0 ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>✓</div>
          <div className={styles.emptyTitle}>Aucune action à faire</div>
          <div className={styles.emptyDesc}>
            {actions.length === 0
              ? 'Tout est à jour — bravo !'
              : 'Aucun résultat pour ces filtres. Essaie d\'en retirer un.'}
          </div>
        </div>
      ) : (
        <div className={styles.groupsList}>
          {Object.keys(grouped).map(type => {
            const meta = ACTION_LABELS[type as ActionType];
            const items = grouped[type];
            return (
              <div key={type} className={styles.group}>
                <div className={styles.groupHeader} style={{ borderLeftColor: meta.color }}>
                  <span className={styles.groupIcon} style={{ background: meta.color }}>
                    {meta.icon}
                  </span>
                  <h3 className={styles.groupTitle}>{meta.label}</h3>
                  <span className={styles.groupCount}>{items.length}</span>
                </div>
                <div className={styles.groupItems}>
                  {items.map((a, idx) => (
                    <div
                      key={a.id}
                      className={`${styles.item} ${styles['sev_' + a.severity]}`}
                      style={{ animationDelay: `${idx * 0.03}s` }}
                    >
                      <div className={styles.itemSeverity}>
                        <span className={`${styles.sevDot} ${styles['dot_' + a.severity]}`} />
                        <span className={styles.sevLabel}>{SEVERITY_LABEL[a.severity]}</span>
                      </div>
                      <div className={styles.itemBody}>
                        <div className={styles.itemTitle}>{a.title}</div>
                        <div className={styles.itemMeta}>
                          <span className={styles.metaChip}>📍 {a.depotTitle || '—'}</span>
                          {a.bacTitle && <span className={styles.metaChip}>🛢 {a.bacTitle}</span>}
                          {a.dueHint && <span className={`${styles.metaChip} ${styles.metaChipDue}`}>⏱ {a.dueHint}</span>}
                        </div>
                        <div className={styles.itemDescription}>{a.description}</div>
                      </div>
                      <div className={styles.itemActions}>
                        {isAdmin ? (
                          <button
                            className={styles.resolveBtn}
                            onClick={() => handleResolve(a)}
                          >
                            {a.type === 'relance_depot' ? '✓ Point fait' :
                              a.type === 'info_clients' ? '✓ Info envoyée' :
                              'Voir'}
                          </button>
                        ) : (
                          <span className={styles.readOnlyHint}>Lecture seule</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {modalAction && modalAction.bac && (
        <BacActionModal
          bac={modalAction.bac}
          type={modalAction.type as BacActionType}
          planningEntry={modalAction.planningEntry}
          onSaved={handleModalSaved}
          onCancel={() => setModalAction(null)}
        />
      )}
    </div>
  );
};
